/**
 * Google Analytics 4 (gtag.js) 連携。
 *
 * 測定 ID は環境変数 REACT_APP_GA_MEASUREMENT_ID から注入する。
 * CI は `cp .env.<env>-backend .env` してからビルドするため、
 * 本番 (.env.prd-backend) にのみ ID を置いておけば dev / local では自動的に無効になる。
 *
 * SPA なので gtag の自動 page_view は使わず (send_page_view: false)、
 * ルート遷移ごとに sendPageView を呼ぶ (hooks/use-analytics-page-view)。
 *
 * ## 破ってはいけない PII 方針
 *
 * 1. クエリ文字列とハッシュは一切送らない (normalizePath が切り落とす)
 * 2. document.title は送らない (page_title には正規化パスを入れる)
 * 3. ID セグメントは :id に伏せる (迷ったら伏せる側に倒す)
 * 4. イベントのパラメータには列挙可能な固定値と数値しか入れない
 * 5. page_location / page_title / page_referrer は全イベントで明示的に上書きする
 *
 * 5 が特に重要。gtag.js は `gtag("event", ...)` の全イベントに
 * page_location / page_title / page_referrer を document から自動付与するため、
 * 上書きを忘れると `/crm/talents/<uuid>?q=<人材名>` が生のまま GA4 へ飛び、
 * 1〜3 の方針が呼び出し側の不注意で破れる。trackEvent / trackPageView の内部で
 * 強制的に注入し、呼び出し側からは上書きできない形にしてある。
 */

/** GA4 公式のパラメータ値の文字数上限。超えた分は切り捨てる。 */
const MAX_PAGE_TITLE = 300;
const MAX_PAGE_REFERRER = 420;
const MAX_PAGE_LOCATION = 1000;
const MAX_PARAM_VALUE = 100;

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

const GTAG_SCRIPT_ID = "ga4-gtag-js";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_PATTERN = /^\d+$/;
/** 静的なルートセグメントの形（小文字の語をハイフンで連結）。例: notification-settings */
const STATIC_SEGMENT_PATTERN = /^[a-z]+(?:-[a-z]+)*$/;

/**
 * 送信するイベント名の一覧。
 *
 * イベント名が 40 文字を超えると GA4 のキーイベントとして計上されないため、
 * 追加時は必ず 40 文字以内にすること（テストで固定している）。
 *
 * `file_download` は GA4 の予約イベント名かつ拡張計測の自動イベントと
 * 同一 eventCount に混ざるため使わない（`lp_asset_download` を使う）。
 */
export const EVENT_NAMES = [
  "lp_cta_click",
  "lp_form_start",
  "generate_lead",
  "lp_asset_download",
  "lp_form_error",
  "lp_section_view",
  "lp_roi_calc",
  "lp_faq_open",
  "sign_up",
  "web_vitals",
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

/**
 * CTA の識別子。フッターの法定リンクは CTA ではないので含めない。
 * `pricing_send` は配信 LP の料金カード（プラン「送る」）。
 */
export type LpCtaId =
  | "navbar"
  | "hero_primary"
  | "hero_secondary"
  | "mobile_sticky"
  | "pricing_free"
  | "pricing_starter"
  | "pricing_standard"
  | "pricing_professional"
  | "pricing_enterprise"
  | "pricing_send";

/** CTA の遷移先。実際に存在する遷移先だけを列挙する。 */
export type LpCtaDest = "#cta" | "#pricing" | "/signup" | "/login";

/** 申込フォームのフィールド名（LpCtaForm の FormData のキー）。 */
export type LpFormField =
  | "companyName"
  | "name"
  | "email"
  | "phone"
  | "teamSize";

/** 営業担当者数の選択肢。表示ラベルではなく安定したキーを送る。 */
export type LpTeamSize = "1-5" | "6-15" | "16-50" | "51-plus" | "unselected";

/** フォーム送信失敗の理由。例外メッセージは送らない（この列挙が唯一の真実）。 */
export type LpFormErrorReason =
  | "http_error"
  | "network"
  | "rate_limited"
  | "validation";

/**
 * LP のセクション識別子。検索 LP と配信 LP で共通。
 * `measurement` は配信 LP の「数字の見方」（S8）。検索 LP の `security` と分ける。
 */
export type LpSectionId =
  | "hero"
  | "problem"
  | "solution"
  | "features"
  | "security"
  | "impact"
  | "roi"
  | "testimonials"
  | "comparison"
  | "pricing"
  | "onboarding"
  | "faq"
  | "cta"
  | "measurement";

/** ダウンロード資料の識別子。 */
export type LpAssetId = "whitepaper_ses_2026";

/** サインアップの手段。 */
export type SignUpMethod = "cognito" | "google";

/** Core Web Vitals の指標名。 */
export type WebVitalName = "LCP" | "INP" | "CLS" | "FCP" | "TTFB";

/**
 * イベントごとに許すパラメータ。
 *
 * Record<string, unknown> にせずキーと値を列挙するのは、
 * 会社名・氏名・メール・電話といった自由入力値を型で弾くため。
 * page_location / page_title / page_referrer はここに書かない
 * （trackEvent が強制注入するので呼び出し側からは指定できない）。
 */
type EventParamMap = {
  lp_cta_click: { cta_id: LpCtaId; cta_dest: LpCtaDest };
  lp_form_start: { field: LpFormField };
  generate_lead: { team_size: LpTeamSize };
  lp_asset_download: { asset_id: LpAssetId; gated: boolean };
  lp_form_error: { reason: LpFormErrorReason };
  lp_section_view: { section_id: LpSectionId };
  lp_roi_calc: { engineers: number; hours: number };
  lp_faq_open: { faq_index: number };
  sign_up: { method: SignUpMethod };
  web_vitals: { metric_name: WebVitalName; metric_value: number };
};

/**
 * 直前に GA4 へ送った論理パス。
 *
 * 送信の単位を「マウント」ではなく「論理パスの変化」にするための状態。
 * コンポーネントの再マウントに依存しないので、/settings → /app の再訪も
 * 1 回だけ正しく送れる。イベント送信時の page_location の基準にも使う。
 */
let lastSentLogicalPath: string | null = null;

/**
 * 測定 ID を返す。呼び出し時に環境変数を読む（テストから差し替えられるように）。
 * @return {string} 測定 ID。未設定なら空文字
 */
function measurementId(): string {
  return process.env.REACT_APP_GA_MEASUREMENT_ID ?? "";
}

/**
 * パスセグメントが ID / トークンの類かを判定する。
 *
 * 静的セグメントは小文字の語をハイフンで繋いだ短い形に限られるため、
 * それ以外（UUID・連番・大文字や数字を含む長い文字列・極端に長い文字列）は
 * ID とみなして伏せる。判定に迷う場合は「伏せる」側に倒す。
 * @param {string} segment パスセグメント
 * @return {boolean} ID とみなす場合 true
 */
function isIdSegment(segment: string): boolean {
  if (UUID_PATTERN.test(segment)) {
    return true;
  }
  if (NUMERIC_PATTERN.test(segment)) {
    return true;
  }
  if (segment.length >= 24) {
    return true;
  }
  return segment.length >= 12 && !STATIC_SEGMENT_PATTERN.test(segment);
}

/**
 * 計測用にパスを正規化する（ID セグメントを :id に置換、クエリ・ハッシュは除去）。
 * @param {string} pathname 元のパス（クエリ・ハッシュを含んでいてもよい）
 * @return {string} 正規化済みパス。例: /crm/talents/:id/edit
 */
export function normalizePath(pathname: string): string {
  const pathOnly = pathname.split(/[?#]/)[0];
  const normalized = pathOnly
    .split("/")
    .map(segment => (isIdSegment(segment) ? ":id" : segment))
    .join("/");
  return normalized === "" ? "/" : normalized;
}

/**
 * リファラーを安全な形に落とす。
 *
 * 同一オリジンならパスを正規化して残し、外部オリジンならオリジンだけを残す。
 * 外部サイトの検索結果 URL などにクエリが載るため、パス・クエリは捨てる。
 * オリジンを残すのは GA4 の参照元 / メディア判定に必要だから。
 * @param {string} referrer document.referrer の値
 * @param {string} origin 自サイトのオリジン
 * @return {string | undefined} 安全なリファラー。送らない場合は undefined
 */
export function sanitizeReferrer(
  referrer: string,
  origin: string,
): string | undefined {
  if (!referrer) {
    return undefined;
  }
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return undefined;
  }
  const safe =
    url.origin === origin
      ? `${url.origin}${normalizePath(url.pathname)}`
      : url.origin;
  return safe.slice(0, MAX_PAGE_REFERRER);
}

/**
 * GA4 が有効か（測定 ID が注入されているか）を返す。
 * @return {boolean} 有効な場合 true
 */
export function isAnalyticsEnabled(): boolean {
  return measurementId() !== "";
}

/**
 * gtag.js を読み込み、GA4 を初期化する。アプリ起動時に一度だけ呼ぶ。
 *
 * 測定 ID が無い環境（dev / local / テスト）では何もしない。
 * @return {void}
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled() || typeof window === "undefined") {
    return;
  }
  const id = measurementId();
  const w = window as GtagWindow;
  if (document.getElementById(GTAG_SCRIPT_ID)) {
    return;
  }

  w.dataLayer = w.dataLayer || [];
  // gtag.js は dataLayer に積まれた Arguments オブジェクトを読む仕様のため、
  // 公式スニペットどおり arguments をそのまま push する（rest 引数の配列では解釈されない）。
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer?.push(arguments);
  };

  const script = document.createElement("script");
  script.id = GTAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    id,
  )}`;
  document.head.appendChild(script);

  w.gtag("js", new Date());
  // 自動 page_view は無効化し、ルート遷移フックから明示的に送る
  // （以下のパラメータ名は gtag.js の仕様で snake_case 固定）
  w.gtag("config", id, { send_page_view: false });

  // page_referrer は document から自動付与されるため、
  // 全イベントの既定値として安全な形に固定しておく。
  const referrer = sanitizeReferrer(document.referrer, window.location.origin);
  if (referrer !== undefined) {
    w.gtag("set", { page_referrer: referrer });
  }
}

/**
 * 現在の論理パスを返す。
 *
 * 論理パスは実 URL とは別で、検索 LP は `/` と `/lp` のどちらで開かれても `/lp`
 * として扱う。配信 LP は `/lp/delivery`。2 つの LP のイベントはこの違いで分かれる。
 * まだ page_view を送っていない場合は実 URL を使う。
 * @return {string} 論理パス
 */
function currentLogicalPath(): string {
  if (lastSentLogicalPath !== null) {
    return lastSentLogicalPath;
  }
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

/**
 * page_location / page_title を安全な値で組み立てる。
 *
 * page_path は送らない。GA4 は page_location からパスを導出するため、
 * page_path は UA / gtag レガシーのパラメータで意味を持たない。
 * @param {string} logicalPath 論理パス（正規化前でよい）
 * @return {object} page_location と page_title
 */
function pageParams(logicalPath: string): {
  page_location: string;
  page_title: string;
} {
  const path = normalizePath(logicalPath);
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return {
    page_location: `${origin}${path}`.slice(0, MAX_PAGE_LOCATION),
    // document.title には人材名など個人情報が載りうるため使わない
    page_title: path.slice(0, MAX_PAGE_TITLE),
  };
}

/**
 * イベントパラメータの文字列値を上限で切る（型で防いだ上での二重の歯止め）。
 * @param {object} params イベントパラメータ
 * @return {object} 上限を適用したパラメータ
 */
function clampParams(params: Record<string, unknown>): Record<string, unknown> {
  const clamped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    clamped[key] =
      typeof value === "string" ? value.slice(0, MAX_PARAM_VALUE) : value;
  }
  return clamped;
}

/**
 * カスタムイベントを送信する。
 *
 * page_location / page_title は内部で強制注入するため、params では指定できない
 * （型からも除外してある）。dev / local / テストでは no-op。
 * @param {EventName} name イベント名
 * @param {object} params イベントパラメータ（イベントごとに型が決まる）
 * @return {void}
 */
export function trackEvent<E extends EventName>(
  name: E,
  params: EventParamMap[E],
): void {
  if (!isAnalyticsEnabled() || typeof window === "undefined") {
    return;
  }
  const w = window as GtagWindow;
  if (!w.gtag) {
    return;
  }

  w.gtag("event", name, {
    ...clampParams(params as Record<string, unknown>),
    // 強制注入は必ず最後に置く（呼び出し側の値で上書きされないように）
    ...pageParams(currentLogicalPath()),
  });
}

/**
 * page_view を送信する。
 * @param {string} pathname 遷移先のパス（正規化前でよい）
 * @param {string} [logicalPath] GA4 上で使う論理パス。省略時は pathname
 * @return {void}
 */
export function trackPageView(pathname: string, logicalPath?: string): void {
  if (!isAnalyticsEnabled() || typeof window === "undefined") {
    return;
  }
  const w = window as GtagWindow;
  if (!w.gtag) {
    return;
  }

  // パラメータ名は gtag.js の仕様で snake_case 固定
  w.gtag("event", "page_view", pageParams(logicalPath ?? pathname));
}

/**
 * 論理パスが変わったときだけ page_view を送る。
 *
 * 送信の単位は論理パスであって実 URL ではない。LP は `/lp` と
 * `/`（未認証 + feature flag ON）のどちらで開かれても同じ 1 ページなので、
 * その間の移動では追加の page_view を送らない。
 * @param {string} logicalPath 論理パス（`/lp` / `/app` / 実パス）
 * @return {void}
 */
export function sendPageView(logicalPath: string): void {
  if (lastSentLogicalPath === logicalPath) {
    return;
  }
  lastSentLogicalPath = logicalPath;
  trackPageView(logicalPath, logicalPath);
}

/**
 * モジュールスコープの状態をリセットする（テスト専用）。
 * @return {void}
 */
export function resetAnalyticsStateForTest(): void {
  lastSentLogicalPath = null;
}
